import React from 'react';
import { ExternalLink, ShieldCheck } from 'lucide-react';

const LOCAL_LAW_FINDER_URL = 'https://locallawfinder.com';
const LOCAL_LAW_FINDER_LOGO =
  'https://customer-assets.emergentagent.com/job_promptlab-42/artifacts/qpg2870j_1000008610.png';

/**
 * Premium internal brand spotlight for LocalLawFinder (Young Black Man Inc. product).
 * Positioned as a featured partner / sister-brand block, not a 3rd-party ad.
 */
const LocalLawFinderSpotlight = () => {
  return (
    <section
      className="px-6 md:px-12 pt-16 md:pt-24"
      data-testid="locallawfinder-spotlight"
    >
      <div className="max-w-7xl mx-auto">
        {/* Section eyebrow */}
        <div className="flex items-center gap-3 mb-6">
          <span className="h-px flex-1 bg-gradient-to-r from-transparent via-[#D4AF37]/40 to-transparent" />
          <span className="text-[10px] md:text-xs uppercase tracking-[0.3em] text-[#D4AF37] font-medium whitespace-nowrap">
            Featured Brand · Young Black Man Inc.
          </span>
          <span className="h-px flex-1 bg-gradient-to-r from-transparent via-[#D4AF37]/40 to-transparent" />
        </div>

        {/* Card */}
        <a
          href={LOCAL_LAW_FINDER_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="group relative block overflow-hidden border border-[#D4AF37]/30 hover:border-[#D4AF37] transition-colors"
          style={{
            background:
              'linear-gradient(135deg, rgba(212,175,55,0.06) 0%, rgba(13,13,13,0.95) 55%, rgba(13,13,13,1) 100%)',
          }}
          data-testid="locallawfinder-card"
        >
          {/* Faint shield glow on the right */}
          <div
            className="pointer-events-none absolute inset-y-0 right-0 w-1/2 opacity-20 blur-3xl"
            style={{
              background:
                'radial-gradient(circle at 70% 50%, rgba(212,175,55,0.45), transparent 65%)',
            }}
          />

          <div className="relative grid md:grid-cols-[auto,1fr,auto] items-center gap-8 md:gap-12 p-6 md:p-10">
            {/* Logo */}
            <div className="flex md:block items-center gap-4 md:gap-0">
              <div className="w-24 h-24 md:w-32 md:h-32 bg-[#F5EFE0] rounded-lg p-3 flex items-center justify-center shadow-lg shadow-black/40 flex-shrink-0">
                <img
                  src={LOCAL_LAW_FINDER_LOGO}
                  alt="LocalLawFinder logo — gold shield with cross"
                  className="w-full h-full object-contain"
                  loading="lazy"
                  data-testid="locallawfinder-logo"
                />
              </div>

              {/* Mobile-only: brand name next to logo */}
              <div className="md:hidden">
                <p className="font-['Bebas_Neue'] text-3xl tracking-tight leading-none text-white">
                  LocalLawFinder
                </p>
                <p className="text-xs text-[#D4AF37] uppercase tracking-[0.18em] mt-1">
                  .com
                </p>
              </div>
            </div>

            {/* Copy */}
            <div className="text-left">
              <p
                className="hidden md:block font-['Bebas_Neue'] text-4xl lg:text-5xl tracking-tight leading-none text-white"
                data-testid="locallawfinder-brandname"
              >
                LocalLawFinder<span className="text-[#D4AF37]">.com</span>
              </p>

              <h3 className="font-['Bebas_Neue'] text-2xl md:text-3xl lg:text-4xl tracking-tight leading-tight text-white mt-3">
                Free legal answers.{' '}
                <span className="text-[#D4AF37]">No signup needed.</span>
              </h3>

              <p className="text-sm md:text-base text-[#A3A3A3] leading-relaxed mt-4 max-w-2xl">
                Look up legal questions in plain language, get helpful
                information, and generate smart questions to ask an attorney
                before you call. No subscription. No membership. Just clear
                legal information.
              </p>

              <div className="flex items-center gap-2 mt-4 text-xs text-[#737373]">
                <ShieldCheck size={14} className="text-[#D4AF37]" />
                <span className="uppercase tracking-[0.15em]">
                  A Young Black Man Inc. product
                </span>
              </div>
            </div>

            {/* CTA */}
            <div className="flex md:flex-col items-center md:items-end gap-3 md:min-w-[200px]">
              <span
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#D4AF37] text-black font-medium uppercase tracking-wider text-sm group-hover:bg-[#E5C048] transition-colors w-full md:w-auto justify-center"
                data-testid="locallawfinder-cta"
              >
                Visit LocalLawFinder
                <ExternalLink
                  size={16}
                  className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
              </span>
              <span className="hidden md:block text-[10px] text-[#737373] uppercase tracking-[0.2em]">
                opens in a new tab
              </span>
            </div>
          </div>
        </a>
      </div>
    </section>
  );
};

export default LocalLawFinderSpotlight;
